import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import INotification from "../../../../models/INotification";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import IUiSocketSetup from "../../../../models/IUiSocketSetup";
import IOnGetSupplierShippingCarriersDTO from "../../../../models/DTO/IOnGetSupplierShippingCarriersDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IGetSupplierShippingCarriersDTO from "../../../../models/DTO/IGetSupplierShippingCarriersDTO";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import IShipping_Carrier from "../../../../models/IShipping_Carrier";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../../shared/OwFnInput/OwFnInput";
import OwButton from "../../../shared/OwButton/OwButton";
import IProcessSupplierShippingCarrierDTO from "../../../../models/DTO/IProcessSupplierShippingCarrierDTO";
import { useFormik } from "formik";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import IOnProcessSupplierShippingCarrierDTO from "../../../../models/DTO/IOnProcessSupplierShippingCarrierDTO";

type Props = {
    cancelFn: (refreshData?: boolean) => void;
    setActivityIndicator: (activityIndicator: boolean) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
};

export const EditShippingCarriersSidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    // const [carrierName, setCarrierName] = useState<string>("");
    // const [trackingUrl, setTrackingUrl] = useState<string>("");
    const [shippingCarriers, setShippingCarriers] = useState<IShipping_Carrier[]>([]);
    const [isDisabled, setIsDisabled] = useState(true);
    const [activeCreate, setActiveCreate] = useState(false);
    const [shippingCarrierEditMode, setShippingCarrierEditMode] = useState<{ [key: string]: { editMode: boolean } }>({});

    const formik = useFormik({
        initialValues: {
            name: "",
            trackingUrl: "",
        },
        onSubmit: (values) => {
            createCarrier(values);
        },
        validate: (values) => {
            const errors: any = {};

            if (!values.name) {
                errors.name = "Required";
            }

            if (!values.trackingUrl) {
                // errors.trackingUrl = "Required";
            }

            setIsDisabled(Object.keys(errors).length > 0);

            return errors;
        },
    });

    const getSupplierShippingCarriers = () => {
        const payload: IGetSupplierShippingCarriersDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        props.socket.emit(EndpointsEnum.ShipmentGetSupplierShippingCarriers, payload);

        props.setActivityIndicator(true);
    };

    const onGetSupplierShippingCarriers = (data: IOnGetSupplierShippingCarriersDTO) => {
        setShippingCarriers(data.carriers);

        const newShippingCarrierEditMode: { [key: string]: { editMode: boolean } } = {};

        for (const carrier of data.carriers) {
            newShippingCarrierEditMode[carrier.guid] = { editMode: shippingCarrierEditMode[carrier.guid]?.editMode || false };
        }

        props.setActivityIndicator(false);

        setShippingCarrierEditMode(newShippingCarrierEditMode);
    };

    const createCarrier = (values: { name: string; trackingUrl: string }) => {
        const payload: IProcessSupplierShippingCarrierDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            shippingCarrier: {
                name: values.name,
                tracking_base_url: values.trackingUrl,
                supplier_id: supplierModel.id,
            },
        };

        setActiveCreate(true);

        props.socket.emit(EndpointsEnum.ShipmentProcessSupplierShippingCarrier, payload);
    };

    const saveCarrier = (carrier: IShipping_Carrier) => {
        const payload: IProcessSupplierShippingCarrierDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            shippingCarrier: {
                name: carrier.name,
                tracking_base_url: carrier.tracking_base_url,
                supplier_id: supplierModel.id,
                guid: carrier.guid,
            },
            deleteFlag: false,
        };

        props.socket.emit(EndpointsEnum.ShipmentProcessSupplierShippingCarrier, payload);

        props.setActivityIndicator(true);

        toggleEditMode(carrier);
    };

    const onProcessCarrier = (data: IOnProcessSupplierShippingCarrierDTO) => {
        if (data.operation === "create") {
            const notification: INotification = {
                message: "successfully_created_shipping_carrier",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };

            props.notifyFn(null, notification);

            setActiveCreate(false);
        } else if (data.operation === "delete") {
            const notification: INotification = {
                message: "successfully_deleted_shipping_carrier",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };

            props.notifyFn(null, notification);
            props.setActivityIndicator(false);
        }

        getSupplierShippingCarriers();
    };

    const deleteCarrier = (carrier: IShipping_Carrier) => {
        const payload: IProcessSupplierShippingCarrierDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            shippingCarrier: {
                name: carrier.name,
                tracking_base_url: carrier.tracking_base_url,
                supplier_id: supplierModel.id,
                guid: carrier.guid,
            },
            deleteFlag: true,
        };

        props.socket.emit(EndpointsEnum.ShipmentProcessSupplierShippingCarrier, payload);

        props.setActivityIndicator(true);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.ShipmentOnGetSupplierShippingCarriers, fn: onGetSupplierShippingCarriers },
        { endpoint: EndpointsEnum.ShipmentOnProcessSupplierShippingCarrier, fn: onProcessCarrier },
    ];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        getSupplierShippingCarriers();

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    const toggleEditMode = (carrier: IShipping_Carrier) => {
        const newShippingCarrierEditMode = { ...shippingCarrierEditMode };

        newShippingCarrierEditMode[carrier.guid] = { editMode: !shippingCarrierEditMode[carrier.guid]?.editMode };

        setShippingCarrierEditMode(newShippingCarrierEditMode);
    };

    const handleChange = (e: ChangeEvent<any>, carrier: IShipping_Carrier) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const newShippingCarriers = shippingCarriers.map((c) => {
            if (c.guid === carrier.guid) {
                return {
                    ...c,
                    [name]: value,
                };
            }

            return c;
        });

        setShippingCarriers(newShippingCarriers);
    };

    return (
        <div className={"EditShippingCarriersSidebar p-2"}>
            <div className={"col-12"}>
                <div className="text-end">
                    <button
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                            props.cancelFn();
                        }}>
                        <i className="far fa-times" />
                    </button>
                </div>
                <OwTitle className={"mb-2"} leftIconName={"fad fa-truck"} title={"shipping_carriers"} />
                <hr />
                <div className={"mb-4"}>
                    <OwLabel title={"shipping_carrier"} isBold={true} />
                    <form onSubmit={formik.handleSubmit}>
                        <div className={"mb-2"}>
                            <OwFnInput placeholder={"carrier_name"} fn={formik.handleChange} name={"name"} value={formik.values.name} />
                        </div>
                        <div className={"mb-2"}>
                            <OwFnInput
                                placeholder={"tracking_url"}
                                fn={formik.handleChange}
                                name={"trackingUrl"}
                                value={formik.values.trackingUrl}
                            />
                        </div>
                        <OwButton title={"add"} type={"submit"} active={activeCreate} disableOnActive={true} disabled={isDisabled} />
                    </form>
                </div>
                <div className={"d-flex flex-column w-100"}>
                    {shippingCarriers.map((carrier) => {
                        return (
                            <div key={carrier.guid} className={"d-flex w-100"}>
                                {shippingCarrierEditMode[carrier.guid]?.editMode ? (
                                    <div className={"me-3"}>
                                        <i
                                            className={"fas fa-check text-success pointer"}
                                            onClick={() => {
                                                saveCarrier(carrier);
                                            }}
                                        />
                                        <i
                                            className={"fas fa-times text-danger pointer ms-2"}
                                            onClick={() => {
                                                toggleEditMode(carrier);
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className={"me-3"}>
                                        <i
                                            className={"fad fa-trash-alt text-danger pointer"}
                                            onClick={() => {
                                                deleteCarrier(carrier);
                                            }}
                                        />

                                        <i
                                            className={"fad fa-pencil text-primary pointer ms-2"}
                                            onClick={() => {
                                                toggleEditMode(carrier);
                                            }}
                                        />
                                    </div>
                                )}

                                {shippingCarrierEditMode[carrier.guid]?.editMode ? (
                                    <div className={"d-flex"}>
                                        <div className={"me-2"}>
                                            <OwFnInput
                                                fn={(e: ChangeEvent<any>) => {
                                                    handleChange(e, carrier);
                                                }}
                                                name={"name"}
                                                value={carrier.name}
                                                className={"me-2"}
                                            />
                                        </div>
                                        <div>
                                            <OwFnInput
                                                fn={(e: ChangeEvent<any>) => {
                                                    handleChange(e, carrier);
                                                }}
                                                name={"tracking_base_url"}
                                                value={carrier.tracking_base_url}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className={"d-flex"}>
                                        <div className={"me-2"}>{carrier.name}</div>
                                        <div>{carrier.tracking_base_url}</div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
